import type { SxProps } from '@mui/material';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';

const Background = styled('figure')(() => ({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: -1,
  margin: 0,
  backgroundColor: '#F1F3F8',
  backgroundImage: 'url("/images/background.svg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
}));

interface Props {
  slotProps?: {
    content?: {
      sx?: SxProps;
    };
    panel?: {
      sx?: SxProps;
    };
  };
  children: ReactNode;
}

export function Layout({ slotProps, children }: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        px: 2,
        pb: 6,
      }}
    >
      <Background />
      <Box
        sx={{
          mt: 25,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          width: '100%',

          ...slotProps?.content?.sx,
        }}
      >
        <a href="/" aria-label="Homepage">
          <svg
            width="212"
            height="50"
            viewBox="0 0 212 50"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M40.4839 26.0476C39.9387 36.6064 31.2002 45 20.5 45C9.79987 45 1.06125 36.6064 0.516074 26.0476H2.87134C10.0314 26.0476 12.5104 28.7957 14.253 35.5023C14.3035 35.6969 14.5889 35.6953 14.6374 35.5004C16.2863 28.8694 18.6113 26.0476 26.4557 26.0476H40.4839ZM40.5 24.3333H26.4557C19.277 24.3333 16.0397 21.3537 14.6187 14.882C14.5752 14.6834 14.2814 14.6738 14.2263 14.8695C12.3314 21.5996 9.69896 24.3333 2.87134 24.3333H1.15612H0.5C0.85185 13.5962 9.6715 5 20.5 5C31.3285 5 40.1482 13.5962 40.5 24.3333Z"
              fill="#063FFE"
            />
            <path
              d="M67.2705 22.1285H64.8082C64.3241 20.4869 62.9561 19.582 61.2514 19.582C58.6417 19.582 57.0843 21.6655 57.0843 25.0539C57.0843 28.4423 58.6207 30.4838 61.2514 30.4838C62.893 30.4838 64.2189 29.6419 64.7661 28.0214H67.2495C66.4708 30.8626 64.0716 32.4621 61.2514 32.4621C57.1685 32.4621 54.6851 29.663 54.6851 25.0539C54.6851 20.4659 57.1895 17.6036 61.3145 17.6036C64.1557 17.6036 66.5971 19.161 67.2705 22.1285ZM71.5754 24.633H69.3235C69.6813 22.1496 71.386 20.6763 74.1851 20.6763C77.2368 20.6763 78.7521 22.4653 78.7521 25.0749V29.4525C78.7521 30.7994 78.8784 31.5781 79.1309 32.1885H76.7738C76.6054 31.7676 76.5423 31.1783 76.5212 30.5469C75.5741 31.8728 74.122 32.4621 72.775 32.4621C70.5021 32.4621 68.9447 31.3887 68.9447 29.242C68.9447 27.7057 69.7865 26.5692 71.4702 26.001C72.9644 25.4959 74.4798 25.3696 76.5002 25.3485V25.096C76.5002 23.4544 75.7425 22.5705 74.0167 22.5705C72.5646 22.5705 71.7859 23.3702 71.5754 24.633ZM71.2176 29.1579C71.2176 30.1049 71.9963 30.6942 73.196 30.6942C75.069 30.6942 76.5002 29.2 76.5002 27.39V26.8849C72.1857 26.927 71.2176 27.8741 71.2176 29.1579ZM81.5759 32.1885V20.971H83.8278V22.4021C84.5433 21.2446 85.6588 20.6763 86.9847 20.6763C88.4789 20.6763 89.5944 21.3288 90.1416 22.6126C90.8782 21.3288 92.0988 20.6763 93.572 20.6763C95.7819 20.6763 97.213 22.0443 97.213 24.6961V32.1885H94.9611V25.1591C94.9611 23.5386 94.3507 22.5494 92.9617 22.5494C91.4464 22.5494 90.5204 24.0437 90.5204 25.6011V32.1885H88.2685V25.1591C88.2685 23.5386 87.6371 22.5494 86.2691 22.5494C84.7538 22.5494 83.8278 24.0437 83.8278 25.6011V32.1885H81.5759ZM102.202 36.8186H99.95V20.971H102.202V22.3179C102.917 21.2656 103.97 20.6763 105.254 20.6763C108.221 20.6763 110.01 22.8862 110.01 26.5482C110.01 30.3575 108.179 32.4621 105.254 32.4621C103.97 32.4621 102.917 31.9359 102.202 30.9047V36.8186ZM104.98 22.5705C103.17 22.5705 102.202 23.9806 102.202 26.5482C102.202 29.1158 103.212 30.5679 104.98 30.5679C106.748 30.5679 107.716 29.1368 107.716 26.5482C107.716 24.0016 106.706 22.5705 104.98 22.5705ZM114.325 24.633H112.073C112.431 22.1496 114.136 20.6763 116.935 20.6763C119.986 20.6763 121.502 22.4653 121.502 25.0749V29.4525C121.502 30.7994 121.628 31.5781 121.88 32.1885H119.523C119.355 31.7676 119.292 31.1783 119.271 30.5469C118.324 31.8728 116.872 32.4621 115.525 32.4621C113.252 32.4621 111.694 31.3887 111.694 29.242C111.694 27.7057 112.536 26.5692 114.22 26.001C115.714 25.4959 117.229 25.3696 119.25 25.3485V25.096C119.25 23.4544 118.492 22.5705 116.766 22.5705C115.314 22.5705 114.535 23.3702 114.325 24.633ZM113.967 29.1579C113.967 30.1049 114.746 30.6942 115.946 30.6942C117.819 30.6942 119.25 29.2 119.25 27.39V26.8849C114.935 26.927 113.967 27.8741 113.967 29.1579ZM124.325 19.582V17.0354H126.577V19.582H124.325ZM124.325 32.1885V20.971H126.577V32.1885H124.325ZM133.34 32.2516C130.478 32.2516 128.752 30.0628 128.752 26.485C128.752 22.7388 130.52 20.6763 133.34 20.6763C134.771 20.6763 135.844 21.1814 136.56 22.1496V20.971H138.812V32.5463C138.812 35.619 136.77 37.1132 134.077 37.1132C131.383 37.1132 129.446 35.8505 129.047 33.2408H131.299C131.509 34.5877 132.582 35.3033 134.056 35.3033C135.55 35.3033 136.56 34.5246 136.56 32.5883V30.6942C135.802 31.7255 134.708 32.2516 133.34 32.2516ZM133.803 30.3575C135.592 30.3575 136.56 28.9895 136.56 26.464C136.56 24.0016 135.55 22.5705 133.803 22.5705C132.014 22.5705 131.046 23.9385 131.046 26.485C131.046 28.9474 132.035 30.3575 133.803 30.3575ZM141.549 32.1885V20.971H143.8V22.4653C144.558 21.2656 145.737 20.6763 147.189 20.6763C149.525 20.6763 150.977 22.0443 150.977 24.6961V32.1885H148.746V25.1591C148.746 23.5386 148.031 22.5494 146.494 22.5494C144.811 22.5494 143.8 24.0437 143.8 25.6011V32.1885H141.549ZM155.505 23.9174C155.505 26.7797 162.681 24.3804 162.681 28.9474C162.681 31.073 160.766 32.4621 158.051 32.4621C155.336 32.4621 153.421 31.1993 153 28.5265H155.252C155.484 29.9366 156.536 30.7153 158.114 30.7153C159.63 30.7153 160.429 30.0418 160.429 29.0947C160.429 26.2956 153.358 28.6528 153.358 24.0437C153.358 22.2969 154.726 20.6763 157.567 20.6763C160.072 20.6763 161.924 21.8339 162.281 24.633H160.03C159.777 23.0545 158.893 22.4653 157.462 22.4653C156.22 22.4653 155.505 23.0756 155.505 23.9174ZM174.375 32.1885L172.144 24.2121L171.913 22.9493L171.66 24.2121L169.429 32.1885H166.651L163.41 20.971H165.83L167.893 29.3052L168.166 30.5679L168.461 29.3052L170.818 20.971H173.196L175.575 29.3052L175.827 30.5048L176.08 29.3052L178.142 20.971H180.457L177.195 32.1885H174.375ZM191.91 28.5265C191.3 31.1362 189.363 32.4621 186.733 32.4621C183.428 32.4621 181.408 30.2522 181.408 26.5903C181.408 22.7809 183.428 20.6763 186.627 20.6763C189.868 20.6763 191.826 22.8651 191.826 26.464V27.0112H183.702C183.828 29.3052 184.902 30.589 186.733 30.589C188.164 30.589 189.174 29.9155 189.553 28.5265H191.91ZM186.627 22.5494C185.007 22.5494 184.039 23.5807 183.765 25.4748H189.49C189.216 23.5807 188.227 22.5494 186.627 22.5494ZM194.081 32.1885V17.0354H196.333V32.1885H194.081ZM199.055 32.1885V17.0354H201.307V32.1885H199.055Z"
              fill="#202430"
            />
          </svg>
        </a>
        <Box
          sx={{
            borderRadius: 4,
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 2px 6px 0px rgba(50, 50, 71, 0.16)',
            px: 4,
            py: 5,
            width: 400,
            maxWidth: '100%',

            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,

            ...slotProps?.panel?.sx,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
